import { Suspense } from "react";
import PeertubeVideoEmbed from "./PeertubeVideoEmbed";
import { ImgForPath } from "../../../lib/images.jsx";
import useToggle from "../../../hooks/useToggle.js";
import { springConfigs, useReveal } from "../../../hooks/useReveal.jsx";
import DefaultPortal from "../../../design-system/utils/DefaultPortal/DefaultPortal.jsx";
import IconButton from "../../../design-system/primitives/components/IconButton/IconButton";
import Button from "../../../design-system/primitives/components/Button/Button";
import { MODELS, useMultiModel } from "../../../api";
import ErrorBoundary from "../../../lib/ErrorBoundary";
import * as styles from "./UploadContent.css";

// The entry's `upload` form field.
type UploadedFile = {
  path: string;
  probe?: { width?: number; height?: number };
};
export type Upload =
  | { type: "video"; data: { id: string } }
  | { type: "link"; data: string }
  | { type: "file"; data: UploadedFile };

// The `coverImage` form field, in either of the shapes it has been stored as.
export type CoverFile = { path?: string; data?: { path?: string } };

const isImage = (file: UploadedFile) => Boolean(file.probe?.width);

/**
 * An uploaded image is shown in full: it takes the card's width until it would
 * grow past 360px, from where it narrows instead. Clicking opens it full-screen.
 */
const UploadedImage = ({ file }: { file: UploadedFile }) => {
  const [open, { toggle, off }] = useToggle();
  const reveal = useReveal(open, {
    config: springConfigs.quickNoBounce,
  });
  return (
    <>
      <ImgForPath
        path={file.path}
        // Attributes, not a resize: they give the browser the aspect ratio
        // before the file arrives.
        width={file.probe?.width}
        height={file.probe?.height}
        className={styles.uploadedImage}
        alt="Projekt Bild"
        onClick={toggle}
      />
      {reveal(() => (
        <DefaultPortal>
          <div className={styles.previewContainer} onClick={off}>
            <div className={styles.previewOnlyButton}>
              <IconButton
                onClick={off}
                icon="x"
                variant="secondary"
                size="large"
                ariaLabel="Schließen"
              />
            </div>
            <ImgForPath path={file.path} className={styles.previewFullView} />
          </div>
        </DefaultPortal>
      ))}
    </>
  );
};

type EntryMediaProps = {
  upload?: Upload | null;
  coverImage?: CoverFile | null;
};

/**
 * What the entry leads with: an uploaded image or video, or else the cover.
 * Links and other files aren't media — they're the `ProjectLinkButton`.
 */
export const EntryMedia = ({ upload, coverImage }: EntryMediaProps) => {
  if (upload?.type === "video") {
    return (
      <div className={styles.video}>
        <PeertubeVideoEmbed data={upload.data} />
      </div>
    );
  }
  if (upload?.type === "file" && isImage(upload.data)) {
    return <UploadedImage file={upload.data} />;
  }
  const coverPath = coverImage?.path || coverImage?.data?.path;
  if (!coverPath) return null;
  return (
    <ImgForPath
      path={coverPath}
      width={556}
      height={280}
      className={styles.coverImg}
      alt="Projekt Vorschaubild"
    />
  );
};

const title = "Projektlink";

const FileLinkButtonInner = ({ path }: { path: string }) => {
  const [modelData] = useMultiModel([MODELS.S3_FILE_URL(path)]);
  const src = modelData?.fileUrl;
  if (!src) return <Button title={title} iconRight="link-2" disabled />;
  return <Button title={title} iconRight="link-2" href={src} target="_blank" />;
};

/**
 * The file's URL is looked up first, so the button waits disabled until then.
 * A missing upload (the `/upload` lookup 404s) drops the button rather than
 * throwing up to `RouteBoundary` and blanking the page. Keyed by `path` so
 * switching files resets the boundary.
 */
const FileLinkButton = ({ path }: { path: string }) => (
  <ErrorBoundary key={path} fallback={null}>
    <Suspense fallback={<Button title={title} iconRight="link-2" disabled />}>
      <FileLinkButtonInner path={path} />
    </Suspense>
  </ErrorBoundary>
);

/** Opens a link or non-image file upload; nothing for any other upload. */
export const ProjectLinkButton = ({ upload }: { upload?: Upload | null }) => {
  if (upload?.type === "link") {
    return (
      <Button
        title={title}
        color="accent"
        iconRight="link-2"
        href={upload.data}
        target="_blank"
      />
    );
  }
  if (upload?.type === "file" && !isImage(upload.data)) {
    return <FileLinkButton path={upload.data.path} />;
  }
  return null;
};
